/**
 * Single source of truth for branding, contact routing and market copy.
 *
 * ⚠ REVIEW BEFORE LAUNCH — items marked TODO are placeholders written without
 * the owner's input. Nothing here claims years of commercial experience, deal
 * counts, square footage transacted, or client outcomes, because those would
 * be invented. Fill them in with real numbers or leave them out.
 *
 * This is the commercial-focused sibling of the residential site (brand
 * "Fourcities") — same agent, same brokerage, same
 * four cities. Kept as a separate site/codebase because the audience, the
 * services, and the visual language are different enough to want their own
 * home rather than a tab bolted onto the residential one.
 */
export const site = {
  brand: {
    name: 'Pinnacle Commercial',
    legalName: 'Pinnacle Commercial · Hometon Inc., Brokerage',
    /** Fallback initials if a wordmark/logo image isn't used. */
    initials: 'PC',
    tagline: 'Commercial Real Estate — Kitchener, Waterloo, Cambridge & Guelph',
    description:
      'Sales, leasing and business listings across the Waterloo Region and Guelph — one commercial-focused agent, from the first call to closing.',
  },

  agent: {
    name: 'Pinnacle Commercial',
    shortName: 'Pinnacle Commercial',
    /** Ontario has one salesperson registration class — "commercial" is a practice focus, not a separate license. */
    title: 'Real Estate Salesperson',
    /** Ontario advertising rules require the brokerage's registered name. */
    brokerage: 'Hometon Inc., Brokerage',
    brokerageCity: 'Markham, Ontario',
    /**
     * RECO registration number. Null until the real one is known — every place
     * that renders it skips it when null, so the site never advertises a
     * placeholder. Set it to e.g. 'RECO Reg. #1234567' and it reappears in the
     * footer title block, the About credentials, and the Contact panel.
     *
     * Note this is not what makes the site RECO-compliant: the requirement is
     * that the brokerage's registered name is at least as prominent as the
     * salesperson's, and that holds with or without this field.
     */
    registration: null as string | null,
    /** TODO: drop a headshot in public/ and point this at it, e.g. '/headshot.jpg'. */
    headshot: null as string | null,
    phone: '(647) 545-6331',
    /** TODO: rewrite in your own words. Deliberately makes no claims I can't verify. */
    bio: [
      'Commercial real estate across the Waterloo Region and Guelph means four different city halls, four different zoning frameworks, and owners, tenants and buyers who need a straight answer fast — not a market report.',
      'I handle sales, leasing and business listings across Cambridge, Kitchener, Waterloo and Guelph directly. One point of contact from the first call to closing — no team lead, no coordinator you have never spoken to.',
    ],
  },

  /**
   * General geography, not market statistics. No vacancy rates, cap rates or
   * absorption numbers here — none of that is data I actually have, and a
   * made-up number is worse than no number.
   */
  serviceAreas: [
    {
      slug: 'cambridge',
      name: 'Cambridge',
      blurb:
        'Industrial and flex space along the 401 corridor, plus a working downtown core in Galt and Hespeler with ground-floor retail and office over top.',
    },
    {
      slug: 'kitchener',
      name: 'Kitchener',
      blurb:
        'The region\u2019s largest and most varied commercial base — a downtown core in active redevelopment, and industrial land toward the 401 and Highway 8.',
    },
    {
      slug: 'waterloo',
      name: 'Waterloo',
      blurb:
        'Office and flex demand anchored by the tech sector and both universities, concentrated around the ION line and the uptown core.',
    },
    {
      slug: 'guelph',
      name: 'Guelph',
      blurb:
        'Its own board and its own market, not a Cambridge suburb — a self-contained city with industrial land east of downtown and a walkable commercial core.',
    },
  ],

  services: [
    {
      slug: 'buying-selling',
      name: 'Buying & Selling',
      short: 'Commercial sales',
      summary:
        'Representation for buyers and sellers of office, retail, industrial, multi-family and land across the four cities.',
      points: [
        'Buyer representation — site search, financial and physical due diligence, offer strategy',
        'Seller representation — pricing, marketing and negotiation through to closing',
        'Office, retail, industrial, multi-residential (5+ units) and vacant/development land',
        'Coordination with your lawyer, lender and (where relevant) your accountant',
      ],
    },
    {
      slug: 'leasing',
      name: 'Leasing',
      short: 'Landlord & tenant representation',
      summary:
        'Representation for landlords filling vacant space and for tenants finding and negotiating the right one.',
      points: [
        'Landlord representation — positioning, marketing and tenant sourcing for vacant space',
        'Tenant representation — site selection and lease negotiation, from first-time tenants to relocations',
        'Gross, net (NNN) and percentage lease structures explained plainly before you sign anything',
        'Renewal and re-negotiation support for existing tenancies',
      ],
    },
    {
      slug: 'business-listings',
      name: 'Business Listings',
      short: 'Buying or selling a business',
      summary:
        'Representation for owners selling a business, and for buyers acquiring one — especially where real estate or a lease is part of the deal.',
      points: [
        'Confidential marketing — details go out under NDA, not on a public listing',
        'Businesses sold with real property attached, or with an assignable/renewable lease',
        'Coordination with your accountant and lawyer on valuation and deal structure',
        'A single point of contact across both the business and any real estate component',
      ],
    },
  ],

  leads: {
    fromEmail: 'leads@pinnaclekwc.com',
    subjectPrefix: '[Pinnacle Inquiry]',
  },

  legal: {
    /** Ontario is regulated by RECO under TRESA — applies to commercial and residential trades alike. */
    regulator: 'Regulated by the Real Estate Council of Ontario (RECO) under TRESA.',
  },
} as const;

export type Site = typeof site;
